// ===== Packages =====

import styled, { DefaultTheme }     from 'styled-components';
import { ReactSVG }                 from 'react-svg';

// ===== Services =====

import {
    setColorLightness,
}                                   from '../../services';

import {
    IBookInvitesSummaryItemProps,
    IBookInvitesSummaryLabelProps,
    IBookInvitesSummaryValueProps,
    IBookInvitesAssignmentStatusProps,
    IBookInvitesAssignmentMetaProps,
}                                   from '../../interfaces';
import {
    BOOK_INVITE_SUMMARY_ACCENT,
    BOOK_INVITE_ASSIGNMENT_ACCENT,
}                                   from '../../enums';

// ===== Constants =====

import {
    USER_PROFILE_DIALOG_Z_INDEX,
    CART_BUTTON_HEIGHT_MULTIPLIER,
    CART_ICON_CONTAINER_PADDING_MULTIPLIER,
    CART_ICON_MARGIN_MULTIPLIER,
    BUTTON_ICON_MULTIPLIER,
    USER_PROFILE_AVATAR_LENGTH_MULTIPLIER,
    USER_PROFILE_DIALOG_AVATAR_BUFFER_MULTIPLIER,
    DEFAULT_FONT_SIZE,
    BODY_FONT_SIZE,
}                                   from '../../constants/generalConstants';
import FONT_TYPE                    from '../../constants/fontType';

const CONTENT_MARGIN_TOP = 0.75; // em

interface ContainerProps {
    isExpanded: boolean,
    lightBackground: boolean,
    transitionDuration: number,
}
export const Container = styled.div<ContainerProps>`
    position: fixed;
    top: 0px;
    right: ${`${CART_BUTTON_HEIGHT_MULTIPLIER + 2 * CART_ICON_CONTAINER_PADDING_MULTIPLIER + CART_ICON_MARGIN_MULTIPLIER}em`};
    z-index: ${USER_PROFILE_DIALOG_Z_INDEX};
    background: ${({
        isExpanded,
        lightBackground,
        theme,
    }) => (isExpanded || lightBackground
        // The pages who you want to have a white custom cursor should route here
        // Dark background invokes white cursor
        ? theme.color.white
        : theme.verascopeColor.purple200
    )};
    transition: ${({ transitionDuration, theme }) => `
        right ${transitionDuration}ms ${theme.motion.eagerEasing}
    `};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        right: ${({ isExpanded }) => (isExpanded
        ? '0px'
        : `${CART_BUTTON_HEIGHT_MULTIPLIER + 2 * CART_ICON_CONTAINER_PADDING_MULTIPLIER + CART_ICON_MARGIN_MULTIPLIER}em`
    )};
    }
`;

interface DialogBodyContainerProps {
    contractedTop: number,
    contractedRight: number,
    expandedTop: number,
    expandedRight: number,
    avatarLength: number,
    expandedWidth: number,
    expandedHeight: number,
    isExpanded: boolean,
    isMobileBookInvitesFullscreen: boolean,
    closeButtonContainerLength: number,
    padding: number,
    transitionDuration: number,
}
export const DialogBodyContainer = styled.div<DialogBodyContainerProps>`
    position: absolute;
    top: ${({ isExpanded, contractedTop, expandedTop }) => `${isExpanded ? expandedTop : contractedTop}px`};
    right: ${({ isExpanded, contractedRight, expandedRight }) => `${isExpanded ? expandedRight : contractedRight}px`};
    background: ${({ theme }) => theme.color.white};
    box-shadow: ${({ isExpanded, theme }) => (isExpanded
        ? theme.color.boxShadow300
        : 'none'
    )};
    border-radius: ${({ closeButtonContainerLength }) => `${closeButtonContainerLength / 2}px`};
    border-top-right-radius: ${({ isExpanded, closeButtonContainerLength }) => `${isExpanded ? 0 : closeButtonContainerLength / 2}px`};
    width: ${({ isExpanded, avatarLength, expandedWidth }) => `${isExpanded ? expandedWidth : avatarLength}px`};
    height: ${({ isExpanded, avatarLength, expandedHeight }) => `${isExpanded ? expandedHeight : avatarLength}px`};
    padding: ${({ isExpanded, padding }) => `${isExpanded ? padding : 0}px`};
    pointer-events: ${({ isExpanded }) => (isExpanded ? 'auto' : 'none')};
    transition: ${({ transitionDuration, theme }) => `
        top ${transitionDuration}ms ${theme.motion.eagerEasing},
        right ${transitionDuration}ms ${theme.motion.eagerEasing},
        border-top-right-radius ${transitionDuration}ms ${theme.motion.eagerEasing},
        border-top-left-radius ${transitionDuration}ms ${theme.motion.eagerEasing},
        width ${transitionDuration}ms ${theme.motion.eagerEasing},
        height ${transitionDuration}ms ${theme.motion.eagerEasing},
        padding ${transitionDuration}ms ${theme.motion.eagerEasing},
        box-shadow ${transitionDuration}ms ${theme.motion.eagerEasing}
    `};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        top: ${({ isExpanded, contractedTop }) => `${isExpanded ? 0 : contractedTop}px`};
        right: ${({ isExpanded, contractedRight }) => {
        if (isExpanded) {
            return '0px';
        }

        return `${contractedRight}px`;
    }};
        width: ${({ isExpanded, avatarLength }) => `${isExpanded ? '100vw' : `${avatarLength}px`}`};
        height: ${({
        isExpanded,
        avatarLength,
        expandedHeight,
        isMobileBookInvitesFullscreen,
    }) => {
        if (isExpanded && isMobileBookInvitesFullscreen) {
            return '100dvh';
        }
        return `${isExpanded ? expandedHeight : avatarLength}px`;
    }};
        border-top-left-radius: ${({ isExpanded, isMobileBookInvitesFullscreen, closeButtonContainerLength }) => `${isExpanded || isMobileBookInvitesFullscreen ? 0 : closeButtonContainerLength / 2}px`};
    }   
`;

interface DialogBodyProps {
    isExpanded: boolean,
    fillHeight: boolean,
    opacityDuration: number,
    transitionDelayDuration: number,
}
export const DialogBody = styled.div<DialogBodyProps>`
    box-sizing: border-box;
    padding-bottom: ${`${0.5 * BODY_FONT_SIZE}px`};
    height: ${({ fillHeight }) => (fillHeight ? '100%' : 'auto')};
    display: ${({ fillHeight }) => (fillHeight ? 'flex' : 'block')};
    flex-direction: ${({ fillHeight }) => (fillHeight ? 'column' : 'initial')};
    opacity: ${({ isExpanded }) => (isExpanded ? 1 : 0)};
    transition: ${({
        isExpanded,
        opacityDuration,
        transitionDelayDuration,
        theme,
    }) => `
        opacity ${opacityDuration}ms ${isExpanded ? transitionDelayDuration : 0}ms ${theme.motion.standardEasing}
    `};
`;

interface CornerCurveProps {
    isExpanded: boolean,
    closeButtonContainerLength: number,
    transitionDuration: number,
}
export const CornerCurve = styled.div<CornerCurveProps>`
    position: absolute;
    right: ${({ isExpanded, closeButtonContainerLength }) => `${isExpanded ? closeButtonContainerLength : 0}px`};
    top: 0px;
    width: ${({ isExpanded, closeButtonContainerLength }) => `${isExpanded ? (closeButtonContainerLength / 2) : 0}px`};
    height: ${({ isExpanded, closeButtonContainerLength }) => `${isExpanded ? (closeButtonContainerLength / 2) : 0}px`};
    transform: translateY(-100%) rotate(180deg);
    pointer-events: none;
    transition: ${({ transitionDuration, theme }) => `
        right ${transitionDuration}ms ${theme.motion.eagerEasing},
        width ${transitionDuration}ms ${theme.motion.eagerEasing},
        height ${transitionDuration}ms ${theme.motion.eagerEasing}
    `};

    & div {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
    }

    & svg {
        width: 100%;
        height: 100%;
    }

    & path {
        fill: ${({ theme }) => theme.color.white};
    }
`;

interface CloseButtonContainerProps {
    contractedTop: number,
    contractedRight: number,
    expandedTop: number,
    expandedRight: number,
    avatarLength: number,
    length: number,
    isExpanded: boolean,
    transitionDuration: number,
}
export const CloseButtonContainer = styled.div<CloseButtonContainerProps>`
    position: absolute;
    top: ${({ isExpanded, contractedTop, expandedTop }) => `${isExpanded ? `${expandedTop}px` : `${contractedTop}px`}`};
    right: ${({ isExpanded, contractedRight, expandedRight }) => `${isExpanded ? `${expandedRight}px` : `${contractedRight}px`}`};
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => theme.color.white};
    width: ${({ length, avatarLength, isExpanded }) => (isExpanded
        ? `${length}px`
        : `${avatarLength}px`
    )};
    height: ${({ length, avatarLength, isExpanded }) => (isExpanded
        ? `${length}px`
        : `${avatarLength}px`
    )};
    border-radius: ${({ length }) => `${(length) / 2}px`};
    border-bottom-right-radius: ${({ length, isExpanded }) => (isExpanded
        ? '0px'
        : `${length / 2}px`
    )};
    border-bottom-left-radius: ${({ length, isExpanded }) => (isExpanded
        ? '0px'
        : `${length / 2}px`
    )};
    transition: ${({ transitionDuration, theme }) => `
        top ${transitionDuration}ms ${theme.motion.eagerEasing},
        right ${transitionDuration}ms ${theme.motion.eagerEasing},
        border-top-right-radius ${transitionDuration}ms ${theme.motion.eagerEasing},
        border-top-left-radius ${transitionDuration}ms ${theme.motion.eagerEasing}
    `};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        right: ${({
        isExpanded,
        expandedRight,
        contractedRight,
    }) => {
        if (isExpanded) {
            return `${expandedRight + (CART_BUTTON_HEIGHT_MULTIPLIER + 2 * CART_ICON_CONTAINER_PADDING_MULTIPLIER + CART_ICON_MARGIN_MULTIPLIER) * BODY_FONT_SIZE}px`;
        }

        return `${contractedRight}px`;
    }};
    }
`;

interface AvatarOverlayProps {
    length: number,
    uploadingImage: boolean,
    transitionDuration: number,
}
export const AvatarOverlay = styled.div<AvatarOverlayProps>`
    width: ${({ length }) => `${length}px`};
    height: ${({ length }) => `${length}px`};
    border-radius: ${({ length }) => `${length / 2}px`};
    background: ${({ theme }) => theme.verascopeColor.purple200};
    opacity: ${({ uploadingImage }) => (uploadingImage ? 1 : 0)};
    pointer-events: none;
    z-index: 1;
    transition: ${({ transitionDuration, theme }) => `
        opacity ${transitionDuration}ms ${theme.motion.eagerEasing}
    `};

    & svg {
        width: ${`${0.9375 * BODY_FONT_SIZE}px`};
        height: ${`${0.9375 * BODY_FONT_SIZE}px`};
    }

    & fill {
        background: ${({ theme }) => theme.color.white};
    }
`;

interface AvatarContainerProps {
    contractedTop: number,
    contractedRight: number,
    expandedTop: number,
    expandedRight: number,
    length: number,
    isExpanded: boolean,
    anonIconId: string,
    avatarLength: number,
    dialogBodyPadding: number,
    translateDuration: number,
    customCursorActive: boolean,
}
export const AvatarContainer = styled.div<AvatarContainerProps>`
    position: absolute;
    top: ${({ isExpanded, contractedTop, expandedTop }) => `${isExpanded ? `${expandedTop}px` : `${contractedTop}px`}`};
    right: ${({ isExpanded, contractedRight, expandedRight }) => `${isExpanded ? `${expandedRight}px` : `${contractedRight}px`}`};
    width: ${({ length }) => `${length}px`};
    height: ${({ length }) => `${length}px`};
    background: ${({ theme }) => theme.verascopeColor.orange200};
    border-radius: ${({ length }) => `${length / 2}px`};
    z-index: 1;
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'default' : 'pointer')};
    transition: ${({
        translateDuration,
        theme,
    }) => `
        top ${translateDuration}ms ${theme.motion.eagerEasing},
        right ${translateDuration}ms ${theme.motion.eagerEasing},
        width ${translateDuration}ms ${theme.motion.eagerEasing},
        height ${translateDuration}ms ${theme.motion.eagerEasing}
    `};

    & > div {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    & ${({ anonIconId }) => `#${anonIconId}`} svg {
        width: ${({ length }) => `${length - 0.3125 * BODY_FONT_SIZE}px`};
        height: ${({ length }) => `${length - 0.3125 * BODY_FONT_SIZE}px`};
    }

    & svg {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    & path {
        fill: ${({ theme }) => theme.color.white};
    }

    &:hover ${AvatarOverlay} {
        opacity: ${({ isExpanded }) => (isExpanded ? 0.6 : 0)};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        top: ${({
        isExpanded,
        contractedTop,
        dialogBodyPadding,
    }) => `${isExpanded ? `${dialogBodyPadding}px` : `${contractedTop}px`}`};
        right: ${({
        isExpanded,
        contractedRight,
        avatarLength,
        dialogBodyPadding,
    }) => {
        if (isExpanded) {
            return `calc(100vw - ${avatarLength + dialogBodyPadding}px)`;
        }

        return `${contractedRight}px`;
    }};
    }
`;

interface AvatarUploadProgressBarContainerProps {
    length: number,
}
export const AvatarUploadProgressBarContainer = styled.div<AvatarUploadProgressBarContainerProps>`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: ${({ length }) => `${length - 5}px`};
    height: 0.5em;
    border-radius: 0.25em;
    overflow: hidden;
    border: ${({ theme }) => `${0.0625 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple400}`};
`;

interface AvatarUploadProgressBarProps {
    progress: number,
}
export const AvatarUploadProgressBar = styled.div<AvatarUploadProgressBarProps>`
    width: ${({ progress }) => `${progress}%`};
    height: 100%;
    background: ${({ theme }) => theme.verascopeColor.blue200};
`;

interface NameContainerProps {
    padding: number,
}
export const NameContainer = styled.div<NameContainerProps>`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    padding-left: ${({ padding }) => `${padding}px`};
    margin-left: ${`${USER_PROFILE_AVATAR_LENGTH_MULTIPLIER + USER_PROFILE_DIALOG_AVATAR_BUFFER_MULTIPLIER}em`};
`;

export const NameText = styled.h3`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 600;
    font-size: 1em;
    color: ${({ theme }) => theme.color.neutral900};
    white-space: nowrap;
    margin: 0;
    margin-right: 0.5em;
`;

interface NameInputContainerProps {
    padding: number,
}
export const NameInputContainer = styled.div<NameInputContainerProps>`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding-left: ${({ padding }) => `${padding}px`};
    margin-left: ${`${USER_PROFILE_AVATAR_LENGTH_MULTIPLIER + USER_PROFILE_DIALOG_AVATAR_BUFFER_MULTIPLIER}em`};
`;

interface NameButtonContainerProps {
    width: number,
}
export const NameButtonContainer = styled.div<NameButtonContainerProps>`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: ${({ width }) => `${width}px`};
    background: ${({ theme }) => theme.color.white};
`;

export const AuthenticationMessage = styled.p`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 400;
    font-size: 0.75em;
    line-height: 1.5em;
    color: ${({ theme }) => theme.color.neutral700};
    max-width: 30ch;
    margin: 0;
    margin-top: ${`${CONTENT_MARGIN_TOP}em`};
    margin-left: ${`${(USER_PROFILE_AVATAR_LENGTH_MULTIPLIER + USER_PROFILE_DIALOG_AVATAR_BUFFER_MULTIPLIER) * BODY_FONT_SIZE}px`};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        width: auto;
        max-width: 40ch;
    }
`;

interface AuthMethodsContainerProps {
    isExpanded: boolean,
}
export const AuthMethodsContainer = styled.div<AuthMethodsContainerProps>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    row-gap: 0.4em;
    margin-top: ${({ isExpanded }) => `${isExpanded ? CONTENT_MARGIN_TOP : 0}em`};
    margin-left: ${`${(USER_PROFILE_AVATAR_LENGTH_MULTIPLIER + USER_PROFILE_DIALOG_AVATAR_BUFFER_MULTIPLIER) * BODY_FONT_SIZE}px`};
    background: ${({ theme }) => theme.color.white};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        width: 100%;
        align-items: center;
        margin-left: 0;
    }
`;

interface AuthMethodProps {
    width: number,
    buttonHeight: number,
    $loading: boolean,
}
export const AuthMethod = styled.div<AuthMethodProps>`
    width: ${({ width }) => `${width}px`};

    & button {
        width: 100%;
    }

    & button section {
        width: 100%;
        align-items: center;
        ${({ $loading, buttonHeight }) => ($loading
        ? `
            display: flex;
            justify-content: center;
        `
        : `
            display: grid;
            grid-template-columns: ${BUTTON_ICON_MULTIPLIER * buttonHeight}px 1fr;
        `
    )};
    }

    ${({ $loading }) => !$loading && `
        & button section > div:first-child {
            grid-column: 1;
            justify-self: center;
        }

        & button section > h3 {
            grid-column: 2;
            justify-self: center;
        }
    `};
`;

interface EmailAuthFieldsContainerProps {
    isVisible: boolean,
}
export const EmailAuthFieldsContainer = styled.div<EmailAuthFieldsContainerProps>`
    position: relative;
    padding-left: ${({ isVisible }) => `${isVisible
        ? (USER_PROFILE_AVATAR_LENGTH_MULTIPLIER + USER_PROFILE_DIALOG_AVATAR_BUFFER_MULTIPLIER) * BODY_FONT_SIZE
        : 0}px`};
    max-width: ${`${20 * BODY_FONT_SIZE}px`};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        width: 100%;
        max-width: none;
        box-sizing: border-box;
        padding-left: ${`${1.8 * BODY_FONT_SIZE}px`};
        padding-top: ${`${0.8 * BODY_FONT_SIZE}px`};
    }
`;

export const EmailAuthInputContainer = styled.div`
    position: relative;
    display: block;
    width: 100%;
    z-index: 1; // To put Tooltip in front of Back Button
`;

interface EmailAuthInputProps {
    isInvalid?: boolean,
    height: number,
}
export const EmailAuthInput = styled.input<EmailAuthInputProps>`
    position: relative;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 500;
    font-size: 0.8em;
    line-height: 1.5em;
    color: ${({ isInvalid, theme }) => (isInvalid
        ? theme.verascopeColor.red200
        : theme.color.neutral800
    )};
    background-color: ${({
        isInvalid,
        theme,
    }) => {
        if (isInvalid) {
            return setColorLightness(
                theme.color.error,
                95,
            );
        }

        return theme.color.neutral200;
    }};
    border: ${({ theme, isInvalid }) => `${0.125 * BODY_FONT_SIZE}px solid ${isInvalid
        ? theme.verascopeColor.red200
        : theme.color.white}`}; 
    width: 100%;
    height: ${({ height }) => `${height}px`};
    margin: 0;
    margin-top: 0.5em;
    padding: 0.2em 1em;
    text-transform: none;
    text-decoration: none;
    border-radius: ${({ height }) => `${height / 2}px`};

    &:focus{
        outline: none;
    }

    &::placeholder {
        color: ${({ theme }) => theme.color.neutral600};
    }

    box-sizing: border-box;

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        &::placeholder {
            text-align: center;
        }
    }
`;

interface NameInputProps {
    width: number,
    height: number,
}
export const NameInput = styled(EmailAuthInput)<NameInputProps>`
    width: ${({ width }) => `${width}px`};
    height: ${({ height }) => `${height}px`};
    margin-top: 0px;
    margin-right: 0.25em;
    padding: 0px 1em;

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        width: ${`calc((100% - ${5 * BODY_FONT_SIZE}px) / 2)`};
    }
`;

export const PasswordStrengthContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-around;
    padding-top: ${`${0.4375 * BODY_FONT_SIZE}px`};
    width: 100%;
`;

interface PasswordStrengthBarProps {
    active: boolean,
    hasErrors: boolean,
}
export const PasswordStrengthBar = styled.div<PasswordStrengthBarProps>`
    display: inline-block;
    width: ${`${1.875 * BODY_FONT_SIZE}px`};
    height: ${`${0.375 * BODY_FONT_SIZE}px`};
    border-radius: ${`${0.1875 * BODY_FONT_SIZE}px`};
    background: ${({ active, hasErrors, theme }) => {
        if (hasErrors && active) return theme.verascopeColor.red200;
        if (active) return theme.verascopeColor.blue200;

        return theme.color.neutral300;
    }};
`;

export const EmailAuthBackButtonContainer = styled.div`
    position: absolute;
    top: 0px;
    left: 0px;
    background: ${({ theme }) => theme.color.white};
`;

interface EmailAuthTabsContainerProps {
    height: number,
}
export const EmailAuthTabsContainer = styled.div<EmailAuthTabsContainerProps>`
    position: relative;
    height: ${({ height }) => `${height}px`};
`;

interface EmailAuthTabButtonContainerProps {
    index: number,
    width: number,
    margin: number,
    background: string,
    movingButtonCurrentIndex: number,
}
export const EmailAuthTabButtonContainer = styled.div<EmailAuthTabButtonContainerProps>`
    position: absolute;
    top: 0px;
    left: ${({ index, width, margin }) => `${index * (width + margin)}px`};
    margin: ${({ margin }) => `0px ${margin / 2}px`};
    
    & h3 {
        color: ${({
        index,
        movingButtonCurrentIndex,
        theme,
    }) => (index === movingButtonCurrentIndex
        ? `${theme.color.white}`
        : 'auto'
    )};
    }

    &:hover button {
        background-color: ${({
        index,
        movingButtonCurrentIndex,
        background,
    }) => (index === movingButtonCurrentIndex
        ? background
        : 'auto'
    )};
    }

`;

interface ForgotPasswordTextProps {
    transitionDuration: number,
}
export const ForgotPasswordText = styled.p<ForgotPasswordTextProps>`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 400;
    font-size: 0.8em;
    color: ${({ theme }) => theme.color.neutral800};
    white-space: nowrap;
    margin: 0;
    padding: ${`${0.3125 * BODY_FONT_SIZE}px 0px`};
    padding-left: ${`${0.9375 * BODY_FONT_SIZE}px`};
    transition: ${({ transitionDuration, theme }) => `
        color ${transitionDuration}ms ${theme.motion.standardEasing},
        font-weight ${transitionDuration}ms ${theme.motion.standardEasing}
    `};

    &:hover {
        color: ${({ theme }) => theme.verascopeColor.purple300};
        font-weight: 600;
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        width: 100%;
        padding-left: 0px;
        text-align: center;
    }
`;

export const SubmitEmailAuthButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    margin-top: ${`${0.3125 * BODY_FONT_SIZE}px`};
`;

interface AuthUserDetailsProps {
    isVisible: boolean,
    padding: number,
    paddingDuration: number,
}
export const AuthUserDetails = styled.div<AuthUserDetailsProps>`
    position: relative;
    display: flex;
    flex-direction: column;
    margin-top: ${({ isVisible }) => (isVisible ? `${1 * BODY_FONT_SIZE}px` : '0px')};
    row-gap: ${`${0.8 * BODY_FONT_SIZE}px`};
    padding-left: ${({ isVisible, padding }) => `${isVisible
        ? padding + (USER_PROFILE_AVATAR_LENGTH_MULTIPLIER + USER_PROFILE_DIALOG_AVATAR_BUFFER_MULTIPLIER) * BODY_FONT_SIZE
        : 0}px`};
    transition: ${({ paddingDuration, theme }) => `
        padding-left ${paddingDuration}ms ${theme.motion.eagerEasing}
    `};
`;

export const AuthUserContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    min-height: 1.3em;
`;

export const AuthUserIcon = styled.div`
    position: relative;
    height: 1em;
    width: 1em;
    margin-right: ${`${0.25 * BODY_FONT_SIZE}px`};

    & div {
        width: 100%;
        height: 100%;
    }

    & svg {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        height: 1em;
        width: 1em;
    }

    & path {
        fill: ${({ theme }) => theme.color.neutral500};
    }
`;

export const AuthUserText = styled.div`
    position: relative;
    margin: 0;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 500;
    font-size: 0.8em;
    color: ${({ theme }) => theme.color.neutral700};
    margin-left: ${`${0.3125 * BODY_FONT_SIZE}px`};
    white-space: nowrap;
    text-transform: none;
`;

export const AuthUserLabelRow = styled.span`
    display: inline-flex;
    align-items: center;
    gap: ${`${0.15 * BODY_FONT_SIZE}px`};
`;

export const BookInvitesSummaryLabelRow = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: ${`${0.86 * BODY_FONT_SIZE}px`};
    line-height: 1;
`;

interface InfoDisclosureButtonContainerProps {
    $bookInvitesSummaryTopRight?: boolean,
}
export const InfoDisclosureButtonContainer = styled.div<InfoDisclosureButtonContainerProps>`
    position: ${({ $bookInvitesSummaryTopRight }) => ($bookInvitesSummaryTopRight ? 'absolute' : 'static')};
    top: ${({ $bookInvitesSummaryTopRight }) => ($bookInvitesSummaryTopRight ? `${0.3 * BODY_FONT_SIZE}px` : 'auto')};
    right: ${({ $bookInvitesSummaryTopRight }) => ($bookInvitesSummaryTopRight ? `${0.22 * BODY_FONT_SIZE}px` : 'auto')};
    z-index: ${({ $bookInvitesSummaryTopRight }) => ($bookInvitesSummaryTopRight ? 1 : 'auto')};
    display: inline-flex;
    width: ${`${0.86 * BODY_FONT_SIZE}px`};
    height: ${`${0.68 * BODY_FONT_SIZE}px`};
    background: ${({ theme }) => theme.color.white};
    align-items: center;
    justify-content: center;
    pointer-events: none;
`;

interface InfoDisclosureButtonProps {
    customCursorActive: boolean,
}
export const InfoDisclosureButton = styled.div<InfoDisclosureButtonProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${`${0.86 * BODY_FONT_SIZE}px`};
    height: ${`${0.68 * BODY_FONT_SIZE}px`};
    margin: 0;
    border-radius: 50%;
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'default' : 'pointer')};
    pointer-events: auto;

    &:focus-visible {
        outline: ${({ theme }) => `${0.08 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple300}`};
        outline-offset: ${`${0.06 * BODY_FONT_SIZE}px`};
    }
`;

interface InfoDisclosureIconProps {
    $accent?: BOOK_INVITE_SUMMARY_ACCENT,
}
export const InfoDisclosureIcon = styled(ReactSVG)<InfoDisclosureIconProps>`
    display: inline-flex;
    width: 100%;
    height: 100%;

    & > div {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    & svg {
        width: 100%;
        height: 100%;
        display: block;
        transform: scale(1.28);
        transform-origin: center;
    }

    & path {
        fill: ${({ theme, $accent }) => getBookInviteSummaryAccentColor(theme, $accent)};
        transition: ${({ theme }) => `fill 120ms ${theme.motion.standardEasing}`};
    }

    ${InfoDisclosureButton}:hover & path,
    ${InfoDisclosureButton}:focus-visible & path {
        fill: ${({ theme, $accent }) => getBookInviteSummaryAccentColor(theme, $accent, true)};
    }
`;

export const AuthUserInfoDisclosureText = styled.p`
    margin: 0;
    margin-top: ${`${0.3 * BODY_FONT_SIZE}px`};
    margin-left: ${`${0.3 * BODY_FONT_SIZE}px`};
    max-width: 32ch;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 0.76em;
    font-weight: 500;
    line-height: 1.5em;
    color: ${({ theme }) => theme.color.neutral700};
    text-transform: none;
`;

interface AuthUserDisclosureContainerProps {
    $labelColumnWidth: number,
}
export const AuthUserDisclosureContainer = styled.div<AuthUserDisclosureContainerProps>`
    position: relative;
    display: grid;
    grid-template-columns: ${({ $labelColumnWidth }) => (
        `calc(1em + ${0.25 * BODY_FONT_SIZE}px) ${$labelColumnWidth > 0 ? `${$labelColumnWidth}px` : 'max-content'} ${0.86 * BODY_FONT_SIZE}px auto`
    )};
    align-items: center;
    column-gap: 0;
    row-gap: ${`${0.15 * BODY_FONT_SIZE}px`};
    width: 100%;
`;

export const AuthUserDisclosureIcon = styled(AuthUserIcon)`
    grid-column: 1;
    grid-row: 1;
    align-self: center;
    margin-right: ${`${0.25 * BODY_FONT_SIZE}px`};
`;

export const AuthUserDisclosureLabel = styled.p.attrs({
    'data-auth-disclosure-label': 'true',
})`
    grid-column: 2;
    grid-row: 1;
    margin: 0;
    margin-left: ${`${0.3125 * BODY_FONT_SIZE}px`};
    margin-right: ${`${0.3125 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 500;
    font-size: 0.8em;
    line-height: 1.35em;
    white-space: nowrap;
    color: ${({ theme }) => theme.color.neutral700};
    text-transform: none;
`;

export const AuthUserDisclosureInfoButtonContainer = styled.div`
    grid-column: 3;
    grid-row: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-self: center;
`;

export const AuthUserDisclosureControl = styled.div`
    grid-column: 4;
    grid-row: 1;
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    margin-left: ${`${0.55 * BODY_FONT_SIZE}px`};
`;

export const AuthUserDisclosureText = styled(AuthUserInfoDisclosureText)`
    grid-column: 2 / 5;
    max-width: none;
    width: 100%;
`;

interface BookInvitesSummaryInfoDisclosureTextProps {
    $accent?: BOOK_INVITE_SUMMARY_ACCENT,
}
export const BookInvitesSummaryInfoDisclosureText = styled.p<BookInvitesSummaryInfoDisclosureTextProps>`
    margin: 0;
    margin-top: ${`${0.75 * BODY_FONT_SIZE}px`};
    max-width: 22ch;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 0.94em;
    font-weight: 500;
    line-height: 1.35em;
    color: ${({ theme, $accent }) => getBookInviteSummaryAccentColor(theme, $accent)};
    text-transform: none;
    text-align: center;
`;

const getBookInviteSummaryAccentColor = (
    theme: DefaultTheme,
    accent?: BOOK_INVITE_SUMMARY_ACCENT,
    emphasized?: boolean,
): string => {
    if (accent == null) {
        return emphasized ? theme.color.neutral700 : theme.color.neutral400;
    }
    if (accent === BOOK_INVITE_SUMMARY_ACCENT.orange) {
        return emphasized ? theme.verascopeColor.orange100 : theme.verascopeColor.orange200;
    }
    if (accent === BOOK_INVITE_SUMMARY_ACCENT.red) {
        return emphasized ? theme.verascopeColor.red100 : theme.verascopeColor.red200;
    }
    if (accent === BOOK_INVITE_SUMMARY_ACCENT.green) {
        return emphasized ? theme.verascopeColor.green100 : theme.verascopeColor.green200;
    }
    return emphasized ? theme.verascopeColor.purple200 : theme.verascopeColor.purple400;
};

export const AuthVerificationContainer = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: ${`${0.4 * BODY_FONT_SIZE}px`};
    background: ${({ theme }) => theme.color.white};

    ${AuthUserDisclosureControl} & {
        margin-left: 0;
    }
`;

interface AuthEmailVerificationBadgeProps {
    height: number,
}
export const AuthEmailVerificationBadge = styled.p<AuthEmailVerificationBadgeProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${`0px ${0.4 * BODY_FONT_SIZE}px`};
    height: ${({ height }) => `${height}px`};
    margin: 0;
    border-radius: ${({ height }) => `${height / 2}px`};
    border: ${({ theme }) => `${0.1 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.purple400}`};
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 700;
    font-size: 0.6em;
    color: ${({ theme }) => theme.verascopeColor.purple400};
    white-space: nowrap;
`;

export const AuthUserRow = styled.div`
    display: flex;
    align-items: center;
    align-self: flex-start;
    margin-left: auto;
`;

export const AuthUserSignOutButtonContainer = styled.div`
    margin-top: 0px;

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        display: flex;
        justify-content: center;
        width: calc(100% + ${(USER_PROFILE_AVATAR_LENGTH_MULTIPLIER + USER_PROFILE_DIALOG_AVATAR_BUFFER_MULTIPLIER) * BODY_FONT_SIZE}px);
        margin-left: -${(USER_PROFILE_AVATAR_LENGTH_MULTIPLIER + USER_PROFILE_DIALOG_AVATAR_BUFFER_MULTIPLIER) * BODY_FONT_SIZE}px;
    }
`;

export const BookInvitesOverlay = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    flex: 1;
    min-height: 0;
    background: ${({ theme }) => theme.color.white};
    margin-top: ${`${1 * BODY_FONT_SIZE}px`};
    padding-left: ${`${(USER_PROFILE_AVATAR_LENGTH_MULTIPLIER + USER_PROFILE_DIALOG_AVATAR_BUFFER_MULTIPLIER) * BODY_FONT_SIZE}px`};
    padding-right: ${`${0.375 * BODY_FONT_SIZE}px`};
    padding-bottom: ${`${0.5 * BODY_FONT_SIZE}px`};
    box-sizing: border-box;

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        padding-left: ${`${1.8 * BODY_FONT_SIZE}px`};
        padding-right: 0;
        padding-bottom: ${`${0.5 * BODY_FONT_SIZE}px`};
    }
`;

export const BookInvitesHeader = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: ${`${0.5 * BODY_FONT_SIZE}px`};
`;

export const BookInvitesTitle = styled.h3`
    margin: 0;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 600;
    font-size: 0.85em;
    color: ${({ theme }) => theme.color.neutral900};
`;

export const BookInvitesBackButtonContainer = styled.div`
    position: absolute;
    top: 50%;
    left: -${`${(
        (USER_PROFILE_AVATAR_LENGTH_MULTIPLIER + USER_PROFILE_DIALOG_AVATAR_BUFFER_MULTIPLIER)
        * BODY_FONT_SIZE
    ) - (0.25 * BODY_FONT_SIZE)}px`};
    transform: translateY(-50%);
    background: ${({ theme }) => theme.color.white};

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        left: -${`${1.8 * BODY_FONT_SIZE}px`};
        transform: translateY(-50%);
    }
`;

export const BookInvitesSummaryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: ${`${0.25 * BODY_FONT_SIZE}px`};
    margin-bottom: ${`${0.2 * BODY_FONT_SIZE}px`};
`;

export const BookInvitesSummaryItem = styled.div<IBookInvitesSummaryItemProps>`
    position: relative;
    display: grid;
    grid-template-rows: auto 1fr;
    align-items: center;
    justify-items: center;
    border: ${({ theme, $accent }) => {
        let borderColor = theme.verascopeColor.purple400;
        if ($accent === BOOK_INVITE_SUMMARY_ACCENT.orange) borderColor = theme.verascopeColor.orange100;
        if ($accent === BOOK_INVITE_SUMMARY_ACCENT.red) borderColor = theme.verascopeColor.red200;
        if ($accent === BOOK_INVITE_SUMMARY_ACCENT.green) borderColor = theme.verascopeColor.green200;
        return `${0.1 * BODY_FONT_SIZE}px solid ${borderColor}`;
    }};
    border-radius: ${`${0.25 * BODY_FONT_SIZE}px`};
    min-height: ${`${2.6 * BODY_FONT_SIZE}px`};
    padding: ${`${0.05 * BODY_FONT_SIZE}px ${0.2 * BODY_FONT_SIZE}px ${0.75 * BODY_FONT_SIZE}px`};
    background: ${({ theme, $hero, $accent }) => {
        if ($hero && $accent === BOOK_INVITE_SUMMARY_ACCENT.orange) {
            return setColorLightness(theme.verascopeColor.orange200, 94);
        }
        if ($hero && $accent === BOOK_INVITE_SUMMARY_ACCENT.red) {
            return setColorLightness(theme.verascopeColor.red200, 95);
        }
        return theme.color.white;
    }};
`;

export const BookInvitesSummaryLabel = styled.div<IBookInvitesSummaryLabelProps>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    row-gap: ${`${0.1 * BODY_FONT_SIZE}px`};
    margin: 0;
    margin-top: ${`${0.2 * BODY_FONT_SIZE}px`};
    margin-bottom: ${`${0.02 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 700;
    font-size: 0.6em;
    color: ${({ theme, $accent }) => {
        if ($accent === BOOK_INVITE_SUMMARY_ACCENT.orange) return theme.verascopeColor.orange100;
        if ($accent === BOOK_INVITE_SUMMARY_ACCENT.red) return theme.verascopeColor.red200;
        if ($accent === BOOK_INVITE_SUMMARY_ACCENT.green) return theme.verascopeColor.green200;
        return theme.verascopeColor.purple400;
    }};
    text-transform: none;
    text-align: center;
`;

export const BookInvitesSummaryValue = styled.p<IBookInvitesSummaryValueProps>`
    margin: 0;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    padding-top: ${`${0.75 * BODY_FONT_SIZE}px`};
    padding-bottom: ${`${0.15 * BODY_FONT_SIZE}px`};
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 700;
    font-size: 0.8em;
    line-height: 1;
    color: ${({ theme, $accent }) => {
        if ($accent === BOOK_INVITE_SUMMARY_ACCENT.orange) return theme.verascopeColor.orange200;
        if ($accent === BOOK_INVITE_SUMMARY_ACCENT.red) return theme.verascopeColor.red200;
        if ($accent === BOOK_INVITE_SUMMARY_ACCENT.green) return theme.verascopeColor.green200;
        return theme.verascopeColor.purple400;
    }};
    text-align: center;
`;

export const BookInvitesSummaryHeroAction = styled.div`
    margin-top: 0;
    margin-bottom: ${`${0.35 * BODY_FONT_SIZE}px`};
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: ${`${0.25 * BODY_FONT_SIZE}px`};
    align-items: center;

    & > *:first-child {
        width: 100%;
    }

    & > button > section {
        width: 100%;
        justify-content: center !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
    }
`;

interface BookInvitesAssignmentsContainerProps {
    $isEmpty: boolean,
    $fillAvailableHeight?: boolean,
}
export const BookInvitesAssignmentsContainer = styled.div<BookInvitesAssignmentsContainerProps>`
    position: relative;
    display: flex;
    flex-direction: column;
    flex: ${({ $fillAvailableHeight }) => ($fillAvailableHeight ? 1 : 'initial')};
    min-height: ${({ $fillAvailableHeight }) => ($fillAvailableHeight ? 0 : 'auto')};
    border-radius: ${`${0.25 * BODY_FONT_SIZE}px`};
    overflow: hidden;
    background: ${({ theme, $isEmpty }) => ($isEmpty ? 'transparent' : theme.color.neutral300)};
    padding: ${({ $isEmpty }) => ($isEmpty
        ? `${1 * BODY_FONT_SIZE}px 0px 0px 0px`
        : `${0.22 * BODY_FONT_SIZE}px`)};
`;

interface BookInvitesAssignmentsListProps {
    $showScrollHint: boolean,
    $fillAvailableHeight?: boolean,
}
export const BookInvitesAssignmentsList = styled.div<BookInvitesAssignmentsListProps>`
    max-height: ${({ $fillAvailableHeight }) => (
        $fillAvailableHeight ? 'none' : `${10.2 * BODY_FONT_SIZE}px`
    )};
    flex: ${({ $fillAvailableHeight }) => ($fillAvailableHeight ? 1 : 'initial')};
    min-height: ${({ $fillAvailableHeight }) => ($fillAvailableHeight ? 0 : 'auto')};
    overflow-y: auto;
    padding-right: ${`${0.125 * BODY_FONT_SIZE}px`};
    padding-bottom: ${({ $showScrollHint }) => `${($showScrollHint ? 1.2 : 0.25) * BODY_FONT_SIZE}px`};
`;

export const BookInvitesAssignmentsScrollHint = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: ${`${1.35 * BODY_FONT_SIZE}px`};
    pointer-events: none;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    background: ${({ theme }) => `linear-gradient(
        to bottom,
        rgba(255, 255, 255, 0) 0%,
        ${theme.color.neutral300} 70%
    )`};
`;

export const BookInvitesAssignmentsScrollHintText = styled.p`
    margin: 0;
    margin-bottom: ${`${0.3 * BODY_FONT_SIZE}px`};
    padding: ${`${0.2 * BODY_FONT_SIZE}px ${0.3 * BODY_FONT_SIZE}px`};
    border-radius: 9999px;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 0.5em;
    font-weight: 600;
    color: ${({ theme }) => theme.color.white};
    background: rgba(0, 0, 0, 0.28);
    text-transform: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${`${0.1 * BODY_FONT_SIZE}px`};
`;

export const BookInvitesAssignmentsScrollHintChevron = styled.span`
    font-size: 0.95em;
    line-height: 1;
`;

export const BookInvitesCreateInviteContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: ${`${0.25 * BODY_FONT_SIZE}px`};
    border: none;
    border-radius: ${`${0.25 * BODY_FONT_SIZE}px`};
    background: ${({ theme }) => theme.color.white};
    box-shadow: ${({ theme }) => theme.color.boxShadow100};
    padding: ${`${0.3 * BODY_FONT_SIZE}px`};
    box-sizing: border-box;
    margin-bottom: ${`${0.25 * BODY_FONT_SIZE}px`};
`;

export const BookInvitesCreateInviteActions = styled.div`
    display: flex;
    justify-content: flex-end;
    flex-wrap: nowrap;
    gap: ${`${0.1 * BODY_FONT_SIZE}px`};
`;

export const BookInvitesCreateInviteActionButtonContainer = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => theme.color.white};
    border-radius: 50%;
`;

export const BookInvitesEmailPreviewContainer = styled.div`
    border: ${({ theme }) => `${0.0625 * BODY_FONT_SIZE}px solid ${theme.color.neutral300}`};
    border-radius: ${`${0.35 * BODY_FONT_SIZE}px`};
    background: ${({ theme }) => theme.color.white};
    padding: ${`${0.35 * BODY_FONT_SIZE}px ${0.45 * BODY_FONT_SIZE}px`};
    margin-top: ${`${0.05 * BODY_FONT_SIZE}px`};
`;

export const BookInvitesAssignmentEmailPreviewContainer = styled(BookInvitesEmailPreviewContainer)`
    margin-top: ${`${0.35 * BODY_FONT_SIZE}px`};
    border-radius: ${`${0.25 * BODY_FONT_SIZE}px`};
`;

export const BookInvitesEmailPreviewHeader = styled.div`
    display: flex;
    align-items: center;
    gap: ${`${0.15 * BODY_FONT_SIZE}px`};
    margin-bottom: ${`${0.25 * BODY_FONT_SIZE}px`};

    & svg {
        width: ${`${1 * DEFAULT_FONT_SIZE}px`};
        height: ${`${1 * DEFAULT_FONT_SIZE}px`};
        display: block;
    }

    & path {
        fill: ${({ theme }) => theme.color.neutral700};
    }
`;

export const BookInvitesEmailPreviewHeaderText = styled.p`
    margin: 0;
    margin-left: ${`${0.2 * DEFAULT_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 0.7em;
    font-weight: 700;
    color: ${({ theme }) => theme.color.neutral800};
    text-transform: none;
`;

export const BookInvitesEmailPreviewSubject = styled.p`
    margin: 0;
    margin-bottom: ${`${0.18 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 0.6em;
    font-weight: 700;
    color: ${({ theme }) => theme.color.neutral800};
    text-transform: none;
`;

export const BookInvitesEmailPreviewBody = styled.pre`
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
    max-height: ${`${6 * BODY_FONT_SIZE}px`};
    overflow-y: auto;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 0.6em;
    font-weight: 500;
    line-height: 1.5em;
    color: ${({ theme }) => theme.color.neutral700};
    text-transform: none;
`;

export const BookInvitesEmailPreviewLoadingContainer = styled.div`
    display: flex;
    justify-content: center;
`;

export const BookInvitesEmailPreviewSpinner = styled(ReactSVG)`
    display: inline-flex;

    & > div {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    & svg {
        width: ${`${1.25 * BODY_FONT_SIZE}px`};
        height: ${`${1.25 * BODY_FONT_SIZE}px`};
        animation: rotate 2s linear infinite;
    }

    & path {
        fill: ${({ theme }) => theme.verascopeColor.purple200};
    }
`;

export const BookInvitesCreateInviteField = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: ${`${0.1 * BODY_FONT_SIZE}px`};
`;

interface BookInvitesCreateInviteLabelProps {
    $active: boolean,
    $invalid?: boolean,
}
export const BookInvitesCreateInviteLabel = styled.p<BookInvitesCreateInviteLabelProps>`
    position: absolute;
    left: ${`${0.8 * BODY_FONT_SIZE}px`};
    top: ${({ $active }) => ($active ? `${0.18 * BODY_FONT_SIZE}px` : '50%')};
    transform: translateY(${({ $active }) => ($active ? '0' : '-50%')});
    pointer-events: none;
    z-index: 2;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: ${({ $active }) => ($active ? '0.56em' : '0.78em')};
    font-weight: 700;
    color: ${({ theme, $active, $invalid }) => {
        if ($invalid) return theme.verascopeColor.red200;
        if ($active) return theme.color.neutral700;
        return theme.color.neutral400;
    }};
    text-transform: none;
    margin: 0;
    transition: top 180ms ${({ theme }) => theme.motion.eagerEasing},
        transform 180ms ${({ theme }) => theme.motion.eagerEasing},
        font-size 180ms ${({ theme }) => theme.motion.eagerEasing},
        color 180ms ${({ theme }) => theme.motion.eagerEasing};
`;

interface BookInvitesCreateInviteInputProps {
    $invalid?: boolean,
    $active?: boolean,
}
export const BookInvitesCreateInviteInput = styled.input<BookInvitesCreateInviteInputProps>`
    width: 100%;
    border: ${({ theme, $invalid }) => (
        $invalid ? `${0.0625 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.red200}` : `${0.0625 * BODY_FONT_SIZE}px solid ${theme.color.neutral300}`
    )};
    border-radius: ${`${0.6 * BODY_FONT_SIZE}px`};
    background: ${({ theme, $invalid }) => (
        $invalid ? theme.verascopeColor.red400 : theme.color.white
    )};
    padding: ${({ $active }) => ($active
        ? `${0.8 * BODY_FONT_SIZE}px ${0.8 * BODY_FONT_SIZE}px ${0.22 * BODY_FONT_SIZE}px`
        : `${0.3 * BODY_FONT_SIZE}px ${0.8 * BODY_FONT_SIZE}px`
    )};
    min-height: ${`${1.45 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 0.85em;
    font-weight: 500;
    color: ${({ theme, $invalid }) => (
        $invalid ? theme.verascopeColor.red200 : theme.color.neutral900
    )};
    text-transform: none !important;
    box-sizing: border-box;

    &::placeholder {
        color: transparent;
        text-transform: none;
    }

    &:focus {
        outline: none;
        box-shadow: none;
    }
`;

interface BookInvitesCreateInviteMessageInputProps {
    $active?: boolean,
}
export const BookInvitesCreateInviteMessageInput = styled.textarea<BookInvitesCreateInviteMessageInputProps>`
    width: 100%;
    border: ${({ theme }) => `${0.0625 * BODY_FONT_SIZE}px solid ${theme.color.neutral300}`};
    border-radius: ${`${0.6 * BODY_FONT_SIZE}px`};
    background: ${({ theme }) => theme.color.white};
    padding: ${({ $active }) => ($active
        ? `${0.8 * BODY_FONT_SIZE}px ${0.8 * BODY_FONT_SIZE}px ${0.22 * BODY_FONT_SIZE}px`
        : `${0.3 * BODY_FONT_SIZE}px ${0.8 * BODY_FONT_SIZE}px`
    )};
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 0.85em;
    font-weight: 500;
    color: ${({ theme }) => theme.color.neutral900};
    text-transform: none !important;
    box-sizing: border-box;
    min-height: ${`${1.45 * BODY_FONT_SIZE}px`};
    resize: none;
    overflow-y: auto;

    &::placeholder {
        color: transparent;
        text-transform: none;
    }

    &:focus {
        outline: none;
        box-shadow: none;
    }
`;

export const BookInvitesCreateInviteError = styled.p`
    margin: ${`${0.05 * BODY_FONT_SIZE}px 0px 0px ${0.575 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-size: 0.5em;
    font-weight: 600;
    color: ${({ theme }) => theme.verascopeColor.red200};
    text-transform: none;
`;

interface BookInvitesAssignmentItemProps {
    $accent?: 'purple' | 'green' | 'red' | 'neutral',
}
export const BookInvitesAssignmentItem = styled.div<BookInvitesAssignmentItemProps>`
    border: none;
    border-radius: ${`${0.25 * BODY_FONT_SIZE}px`};
    background: ${({ theme }) => theme.color.white};
    box-shadow: ${({ theme }) => theme.color.boxShadow100};
    padding: ${`${0.3 * BODY_FONT_SIZE}px`};
    margin-bottom: ${`${0.25 * BODY_FONT_SIZE}px`};
`;

export const BookInvitesAssignmentTop = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${`${0.25 * BODY_FONT_SIZE}px`};
`;

interface BookInvitesAssignmentRecipientProps {
    $accent?: 'purple' | 'green' | 'red' | 'neutral',
}
export const BookInvitesAssignmentRecipient = styled.p<BookInvitesAssignmentRecipientProps>`
    margin: 0;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 600;
    font-size: 0.8em;
    line-height: 1.5em;
    color: ${({ theme }) => theme.color.neutral900};
    overflow-wrap: anywhere;
`;

export const BookInvitesAssignmentStatus = styled.p<IBookInvitesAssignmentStatusProps>`
    position: relative;
    margin: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: ${`${0.65 * BODY_FONT_SIZE}px`};
    padding: ${`${0.12 * BODY_FONT_SIZE}px ${0.55 * BODY_FONT_SIZE}px`};
    border: none;
    background: ${({ theme, $accent }) => {
        if ($accent === BOOK_INVITE_ASSIGNMENT_ACCENT.green) return theme.verascopeColor.green200;
        if ($accent === BOOK_INVITE_ASSIGNMENT_ACCENT.red) return theme.verascopeColor.red200;
        if ($accent === BOOK_INVITE_ASSIGNMENT_ACCENT.purple) return theme.verascopeColor.purple400;
        return theme.color.neutral600;
    }};
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 600;
    font-size: 0.6em;
    color: ${({ theme }) => theme.color.white};
    text-transform: none;
`;

export const BookInvitesAssignmentMeta = styled.p<IBookInvitesAssignmentMetaProps>`
    margin: 0;
    margin-top: ${`${0.5 * BODY_FONT_SIZE}px`};
    margin-bottom: ${`${0.5 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 500;
    font-size: 0.65em;
    line-height: 1.5em;
    color: ${({ theme }) => theme.color.neutral600};
    overflow-wrap: anywhere;
`;

export const BookInvitesAssignmentActions = styled.div`
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: ${`${0.15 * BODY_FONT_SIZE}px`};
    margin-top: ${`${0.35 * BODY_FONT_SIZE}px`};
`;

export const BookInvitesAssignmentActionButtonContainer = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => theme.color.white};
    border-radius: 50%;
`;

export const BookInvitesAssignmentIconButtonContainer = styled.div`
    background: ${({ theme }) => theme.color.white};
`;

export const BookInvitesEmptyText = styled.p`
    margin: 0;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 600;
    font-size: 0.68em;
    color: ${({ theme }) => theme.color.neutral800};
    text-align: center;
`;

export const BookInvitesEmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${`${0.2 * BODY_FONT_SIZE}px`};
    padding: 0;
    background: transparent;
`;

export const BookInvitesEmptyIcon = styled.div`
    width: ${`${1.2 * BODY_FONT_SIZE}px`};
    height: ${`${1.2 * BODY_FONT_SIZE}px`};

    & div {
        width: 100%;
        height: 100%;
    }

    & svg {
        width: 100%;
        height: 100%;
    }

    & path {
        fill: ${({ theme }) => theme.color.neutral500};
    }
`;

export const BookInvitesEmptyHelpText = styled.p`
    margin: 0;
    max-width: 32ch;
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 500;
    font-size: 0.58em;
    line-height: 1.45em;
    color: ${({ theme }) => theme.color.neutral700};
    text-align: center;
`;

export const BookInvitesEmptyActionContainer = styled.div`
    margin-top: ${`${0.5 * BODY_FONT_SIZE}px`};
`;

interface BookInviteRemainingButtonProps {
    customCursorActive: boolean,
    height: number,
}
export const BookInviteRemainingButton = styled.button<BookInviteRemainingButtonProps>`
    display: inline-flex;
    align-items: center;
    gap: ${`${0.125 * BODY_FONT_SIZE}px`};
    height: ${({ height }) => `${height}px`};
    padding: ${`0px ${0.4 * BODY_FONT_SIZE}px`};
    border-radius: ${({ height }) => `${height / 2}px`};
    border: none;
    background: ${({ theme }) => theme.verascopeColor.purple400};
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'default' : 'pointer')};
    transition: ${({ theme }) => `
        background 150ms ${theme.motion.standardEasing}
    `};

    &:hover {
        background: ${({ theme }) => theme.verascopeColor.purple300};
    }
`;

export const BookInviteRemainingLabel = styled.span`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 700;
    font-size: ${`${0.6 * BODY_FONT_SIZE}px`};
    line-height: 1;
    color: ${({ theme }) => theme.color.white};
`;

export const BookInviteRemainingValue = styled.span`
    font-family: ${FONT_TYPE.PLUS_JAKARTA_SANS};
    font-weight: 700;
    font-size: ${`${0.6 * BODY_FONT_SIZE}px`};
    line-height: 1;
    color: ${({ theme }) => theme.verascopeColor.orange200};
`;
